export const BOOKING_REPORT_CHECKLIST_PARTS = [
  { key: "sleepStudyReport", label: "Sleep Study Report" },
  { key: "trialReport", label: "Trial Report" },
  { key: "medicalCertificate", label: "Medical Cert" },
];

const SELF_PAY_VALUES = ["ชำระเงินเอง", "เงินสด"];

const normalizeFileName = (fileName) =>
  String(fileName || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const getBookingReportFiles = (report = {}) => {
  if (Array.isArray(report.reportFile)) return report.reportFile;
  return report.reportFile ? [report.reportFile] : [];
};

const isStorageOnlyReportFile = (report, fileName) =>
  Array.isArray(report.storageOnlyFiles)
    ? report.storageOnlyFiles.includes(fileName)
    : report.storageOnly === true;

export const mergeBookingReportRows = (...reportGroups) => {
  const mergedReports = [];
  const reportIndexByKey = {};

  reportGroups.forEach((reportGroup) => {
    (Array.isArray(reportGroup) ? reportGroup : []).forEach((report) => {
      if (!report || typeof report !== "object") return;

      const id = String(report.id || "").trim();
      const hn = String(report.hn || "").trim();
      const reportFile = getBookingReportFiles(report)
        .map((fileName) => String(fileName || "").trim())
        .filter(Boolean);
      const key = id
        ? `id:${id}`
        : `files:${hn}:${reportFile.join("\\u0000")}`;

      if (!Object.prototype.hasOwnProperty.call(reportIndexByKey, key)) {
        const storageOnlyFiles = reportFile.filter((fileName) =>
          isStorageOnlyReportFile(report, fileName),
        );
        reportIndexByKey[key] = mergedReports.length;
        mergedReports.push({
          ...report,
          hn,
          reportFile,
          storageOnly: storageOnlyFiles.length > 0,
          storageOnlyFiles,
        });
        return;
      }

      const reportIndex = reportIndexByKey[key];
      const existingReport = mergedReports[reportIndex];
      const mergedFiles = Array.from(
        new Set([
          ...getBookingReportFiles(existingReport),
          ...reportFile,
        ]),
      );
      const savedFiles = new Set([
        ...getBookingReportFiles(existingReport).filter((fileName) =>
          !isStorageOnlyReportFile(existingReport, fileName),
        ),
        ...reportFile.filter((fileName) =>
          !isStorageOnlyReportFile(report, fileName),
        ),
      ]);
      const storageOnlyFiles = mergedFiles.filter((fileName) => !savedFiles.has(fileName));

      mergedReports[reportIndex] = {
        ...existingReport,
        ...report,
        id: existingReport.id || report.id,
        hn: String(existingReport.hn || report.hn || "").trim(),
        reportFile: mergedFiles,
        // Track recovered files individually so a Storage scan does not hide
        // deletion for files already listed in the Firestore report record.
        storageOnly: storageOnlyFiles.length > 0,
        storageOnlyFiles,
      };
    });
  });

  return mergedReports;
};

export const getBookingReportPartKey = (fileName) => {
  const normalizedFileName = normalizeFileName(fileName);

  // Check the more specific filename markers before the generic "report"
  // marker so a Trial or Medical Certificate does not pass two rows.
  if (
    normalizedFileName.includes("medical") ||
    normalizedFileName.includes("certificate") ||
    normalizedFileName.includes("cert") ||
    normalizedFileName.includes("ใบรับรองแพทย์")
  ) {
    return "medicalCertificate";
  }

  if (normalizedFileName.includes("trial")) {
    return "trialReport";
  }

  if (normalizedFileName.includes("report")) {
    return "sleepStudyReport";
  }

  return "";
};

export const isLphBookingNode = (node) => {
  const normalizedNode = String(node || "").trim();

  return /\bLPH\b/i.test(normalizedNode) || /ladprao/i.test(normalizedNode);
};

const isChgBookingNode = (node) => {
  const normalizedNode = String(node || "").trim();
  return /^CHG(?:03|09|11|304)?$/i.test(normalizedNode) || /chularat/i.test(normalizedNode);
};

export const getRequiredBookingReportPartKeys = ({
  healthInsurance = "",
} = {}) => {
  if (SELF_PAY_VALUES.includes(String(healthInsurance || "").trim())) {
    return ["sleepStudyReport"];
  }

  return ["sleepStudyReport", "trialReport"];
};

export const getBookingReportChecklist = (reports = [], booking = {}) => {
  const filesByPart = BOOKING_REPORT_CHECKLIST_PARTS.reduce(
    (files, part) => ({ ...files, [part.key]: [] }),
    {},
  );
  const unclassifiedFiles = [];

  (Array.isArray(reports) ? reports : []).forEach((report) => {
    getBookingReportFiles(report).forEach((fileName) => {
      const file = {
        id: report.id,
        hn: String(report.hn || booking.hn || "").trim(),
        fileName,
        storageOnly: isStorageOnlyReportFile(report, fileName),
      };
      const partKey = getBookingReportPartKey(fileName);

      if (partKey) {
        filesByPart[partKey].push(file);
      } else {
        unclassifiedFiles.push(file);
      }
    });
  });

  const requiredPartKeys = getRequiredBookingReportPartKeys(booking);
  const parts = BOOKING_REPORT_CHECKLIST_PARTS.map((part) => ({
    ...part,
    files: filesByPart[part.key],
    required: requiredPartKeys.includes(part.key),
    requiredAfterDoctor: part.key === "medicalCertificate" && (isLphBookingNode(booking.node) || isChgBookingNode(booking.node)) &&
      !SELF_PAY_VALUES.includes(String(booking.healthInsurance || "").trim()),
  }));
  const missingParts = parts.filter(
    (part) => part.required && part.files.length === 0,
  );

  const finalMissingParts = parts.filter((part) =>
    (part.required || part.requiredAfterDoctor) && part.files.length === 0,
  );
  return {
    parts,
    unclassifiedFiles,
    requiredPartKeys,
    missingParts,
    isReady: missingParts.length === 0,
    finalMissingParts,
    isFinalReady: finalMissingParts.length === 0,
  };
};

export const getBookingReportReadinessMap = (
  bookings = [],
  reportsByHn = {},
) =>
  (Array.isArray(bookings) ? bookings : []).reduce(
    (readinessByEventId, booking) => {
      const bookingProps = (booking && booking.extendedProps) || booking || {};
      const eventId = bookingProps.uuid || (booking && booking.id);
      const hn = String(bookingProps.hn || "").trim();

      if (!eventId || !hn) return readinessByEventId;

      const checklist = getBookingReportChecklist(
        reportsByHn[hn] || [],
        bookingProps,
      );

      return {
        ...readinessByEventId,
        [String(eventId)]: {
          isReady: checklist.isFinalReady,
          isSleepStudyReportReady: checklist.parts.some((part) => part.key === "sleepStudyReport" && Array.isArray(part.files) && part.files.length > 0),
          isTrialReportReady: checklist.parts.some((part) => part.key === "trialReport" && Array.isArray(part.files) && part.files.length > 0),
          isMedicalCertificateReady: checklist.parts.some((part) => part.key === "medicalCertificate" && Array.isArray(part.files) && part.files.length > 0),
          missingPartLabels: checklist.finalMissingParts.map((part) => part.label),
          requiredPartLabels: checklist.parts
            .filter((part) => part.required || part.requiredAfterDoctor)
            .map((part) => part.label),
        },
      };
    },
    {},
  );

// A missing result is still unchecked, not a failed checklist.
export const matchesBookingReportFilter = (readiness, filter = "all") => {
  if (filter === "all") return true;
  if (filter === "sleep_study_ready") {
    return Boolean(readiness && (readiness.isSleepStudyReportReady === true ||
      (Array.isArray(readiness.parts) && readiness.parts.some((part) => part.key === "sleepStudyReport" && Array.isArray(part.files) && part.files.length > 0))));
  }
  if (filter === "trial_ready") {
    return Boolean(readiness && (readiness.isTrialReportReady === true ||
      (Array.isArray(readiness.parts) && readiness.parts.some((part) => part.key === "trialReport" && Array.isArray(part.files) && part.files.length > 0))));
  }
  if (filter === "medical_cert_ready") {
    return Boolean(readiness && (readiness.isMedicalCertificateReady === true ||
      (Array.isArray(readiness.parts) && readiness.parts.some((part) => part.key === "medicalCertificate" && Array.isArray(part.files) && part.files.length > 0))));
  }
  if (!readiness) return false;
  // File readiness includes the certificate uploaded after doctor review.
  // The earlier isReady gate still allows sending reports to the doctor.
  const isReady = typeof readiness.isFinalReady === "boolean"
    ? readiness.isFinalReady
    : readiness.isReady;
  if (typeof isReady !== "boolean") return false;
  return isReady === (filter === "ready");
};

export const getSortedBookingReportFiles = (reports = [], booking = {}) => {
  const checklist = getBookingReportChecklist(reports, booking);
  return [...checklist.parts.reduce((files, part) => files.concat(part.files), []), ...checklist.unclassifiedFiles];
};
